import { Fragment, ReactNode } from 'react';

import Workflow from 'models/Workflow';
import { ActionStatus, WorkflowAction } from 'models/WorkflowAction';
import ChevronRight from 'svg/ChevronRight';

import { actionStatusDisplay } from '../StatusBadge';
import StatusIcon, { indicatorColor, statusIndicator } from '../StatusIcon';

import { actionCardFor, hasSlideoutFor } from './actionCards';
import { ActionHandlers } from './handlers';
import { ActionRow, ChevronCell, IconCell, RowMain, RowTitle } from './styles';

/**
 * The shared chrome every action row wears (OLIO-2700): status icon, title, the kind's body, and
 * the slideout affordance. Deliberately contains no per-kind branching — what a kind contributes
 * lives in the actionCards registry, and the body is passed in.
 */
export default function ActionCard({
  action,
  workflow,
  body,
  slideoutOpen,
  onOpenSlideout,
  onCloseSlideout,
  handlers,
}: {
  action: WorkflowAction;
  workflow: Workflow;
  body: ReactNode;
  slideoutOpen: boolean;
  onOpenSlideout: () => void;
  onCloseSlideout: () => void;
  handlers: ActionHandlers;
}) {
  // Slideout-backed rows turn the whole card into the trigger: clickable, chevron pinned far
  // right, and the slideout rendered as a sibling so its controls never nest in the button.
  const hasSlideout = hasSlideoutFor(action);
  const slideout = hasSlideout ? actionCardFor(action).slideout : undefined;

  return (
    <Fragment>
      <ActionRow
        data-testid='action-row'
        data-action-kind={action.kind}
        $flagged={action.effectiveStatus === ActionStatus.FLAGGED}
        $clickable={hasSlideout}
        onClick={hasSlideout ? onOpenSlideout : undefined}>
        <IconCell>
          <StatusIcon indicator={statusIndicator(action)} size={16} />
        </IconCell>
        <RowMain>
          <RowTitle $color={indicatorColor(statusIndicator(action))}>
            {actionStatusDisplay(action.effectiveStatus).label}: {action.displayLabel}
          </RowTitle>
          {body}
        </RowMain>
        {hasSlideout && (
          <ChevronCell>
            <ChevronRight width={14} height={14} />
          </ChevronCell>
        )}
      </ActionRow>
      {slideout?.({ action, workflow, handlers, slideoutOpen, onCloseSlideout })}
    </Fragment>
  );
}
